import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useData } from '../DataContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import Segmented from '../components/Segmented';

const COLORS = ['var(--accent-solid)','var(--teal)','var(--accent-2)','#c4b5fd','#5eead4','#c4b5fd','#a78bfa'];
const PERIODOS = [
  { id: 'hoy',    label: 'Hoy'    },
  { id: 'semana', label: '7 días' },
  { id: 'mes',    label: '30 días'},
  { id: 'custom', label: 'Personalizado' },
];

function fmt(n, tipo='num') {
  if (tipo === 'cop') return `$${Math.round(n).toLocaleString('es-CO')}`;
  if (tipo === 'pct') return `${n}%`;
  if (tipo === 'min') return `${Math.floor(n/60)}:${String(Math.floor(n%60)).padStart(2,'0')}`;
  return n;
}

function exportCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => `"${(r[h]||'').toString().replace(/"/g,"'")}"`).join(','));
  const blob = new Blob([[headers.join(','),...rows].join('\n')], {type:'text/csv'});
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

const METRICAS_INFO = {
  'Tasa contacto':     'De las llamadas hechas, en cuántas alguien contestó (habló o no). = Contestadas / Llamadas × 100',
  'Tasa acuerdo':      'De quienes hablaron con el agente, cuántos llegaron a un acuerdo de pago. = Acuerdos / Hablaron × 100',
  'Efectividad neta':  'Contactos con acuerdo sobre el total cargado en la campaña. = Contactos con acuerdo / Contactos × 100. KPI principal en cobranzas.',
  'Penetración':       'Qué tanto de la base se alcanzó a llamar. = Contactos llamados / Contactos cargados × 100',
  'Costo total':       'Total gastado en las llamadas de esta campaña en el periodo, en COP.',
  'Costo x contacto':  'Cuánto costó cada llamada contestada. = Costo total / Contestadas',
  'Costo x acuerdo':   'Cuánto costó conseguir cada acuerdo. = Costo total / Acuerdos. Mientras menor, mejor.',
  'Deuda con acuerdo': 'Suma de la deuda (columna monto de la base) de los contactos que hicieron acuerdo. Es lo comprometido, no lo ya pagado.',
};

const MetricaTooltip = ({label}) => {
  const [show, setShow] = React.useState(false);
  const info = METRICAS_INFO[label];
  if (!info) return null;
  return (
    <span style={{position:'relative',display:'inline-block',marginLeft:4}}>
      <span onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}
        style={{fontSize:10,color:'var(--text-3)',cursor:'help',background:'var(--surface-2)',borderRadius:'50%',width:14,height:14,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
        ?
      </span>
      {show && (
        <div style={{
          position:'absolute',bottom:'120%',left:'50%',transform:'translateX(-50%)',
          background:'var(--bg)',border:'1px solid var(--accent-solid)',borderRadius:8,padding:'8px 12px',
          width:220,fontSize:11,color:'var(--text-2)',lineHeight:1.5,zIndex:100,
          boxShadow:'0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <div style={{fontWeight:600,color:'var(--accent)',marginBottom:4}}>{label}</div>
          {info}
        </div>
      )}
    </span>
  );
};

const Card = ({label, value, sub, color, onClick}) => (
  <div onClick={onClick} className="ic-card" style={{
    borderRadius:16,
    padding:'16px 18px', cursor: onClick ? 'pointer' : 'default',
    transition:'transform 0.2s ease',
  }}>
    <div style={{fontSize:10,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>{label}</div>
    <div style={{fontSize:22,fontFamily:'Inter,sans-serif',fontWeight:700,color,fontVariantNumeric:'tabular-nums'}}>{value}</div>
    {sub && <div style={{fontSize:11,color:'var(--text-3)',marginTop:3}}>{sub}</div>}
  </div>
);

const Tooltip2 = ({active,payload,label}) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:8,padding:'10px 14px'}}>
      <div style={{fontSize:11,color:'var(--text-3)',marginBottom:6}}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{fontSize:13,color:p.color||p.fill,fontWeight:600}}>
          {p.name}: {typeof p.value==='number' ? p.value.toLocaleString('es-CO') : p.value}
        </div>
      ))}
    </div>
  );
};

// Mismo criterio que core/barridos.clasificar en el servidor.
function clasificarLlamada(c) {
  const st = (c.status || '').trim().toLowerCase();
  const r = (c.resultado_llamada || '').trim().toLowerCase();
  if (st === 'rechazada' || r.includes('rechazó la llamada') || r.includes('rechazo la llamada')) return 'rechazo';
  if (['buzon','buzón','mensaje de voz','mensaje automático','mensaje automatico'].some(k => r.includes(k))) return 'buzon';
  if (r.includes('no contest')) return 'no_contesto';
  if (r.includes('sin respuesta') || r.includes('silencio') || r.includes('sin hablar')) return 'sin_hablar';
  const acuerdo = !(r.includes('sin acuerdo') || r.startsWith('rechazo')) && ['acuerdo','compromiso','promesa','ptp'].some(k => r.includes(k));
  if (acuerdo) return 'acuerdo';
  if (['finalizada','completada','contestada'].includes(st)) return 'contesto';
  return 'otro';
}

export default function Dashboard() {
  const { billing, llamadas, lastUpdate, filtroFechaDesde, setFiltroFechaDesde, filtroFechaHasta, setFiltroFechaHasta } = useData();
  const navigate  = useNavigate();
  const empresa   = localStorage.getItem('nombre_empresa') || '';

  const [periodo, setPeriodo]         = useState('semana');
  const [chartType, setChartType]     = useState('area');
  const [clickFiltro, setClickFiltro] = useState(null);
  const [campanas, setCampanas]       = useState([]);
  const [campSelIds, setCampSelIds]   = useState([]);
  const [analytics, setAnalytics]     = useState([]);
  const [loadingAn, setLoadingAn]     = useState(false);
  const [vistaAnalytics, setVistaAnalytics] = useState('resumen'); // resumen | comparar | barridos | evolucion
  const [conv, setConv] = useState(null); // conversión / PTP rate por campaña
  const [resumen, setResumen] = useState(null); // tarjetas y gráficas sobre TODAS las llamadas del periodo

  // Cargar campañas
  useEffect(() => {
    api.get('/analytics/conversion').then(r => setConv(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/campaigns/').then(r => {
      setCampanas(r.data);
      setCampSelIds(r.data.filter(c => !c.archivado).map(c => c.id));
    }).catch(() => {});
  }, []);

  // Resumen general: lo calcula el servidor sobre todas las llamadas del periodo (antes salía
  // de las últimas 500 llamadas cargadas en pantalla). Se refresca cada minuto.
  const paramsPeriodo = useCallback(() => {
    const params = new URLSearchParams();
    if (periodo === 'hoy') { const d = new Date(); d.setHours(0,0,0,0); params.append('fecha_desde', d.toISOString()); }
    else if (periodo === 'semana') { const d = new Date(); d.setDate(d.getDate()-7); params.append('fecha_desde', d.toISOString()); }
    else if (periodo === 'mes') { const d = new Date(); d.setDate(d.getDate()-30); params.append('fecha_desde', d.toISOString()); }
    else if (periodo === 'custom') {
      if (filtroFechaDesde) params.append('fecha_desde', filtroFechaDesde);
      if (filtroFechaHasta) params.append('fecha_hasta', filtroFechaHasta + 'T23:59:59');
    }
    return params;
  }, [periodo, filtroFechaDesde, filtroFechaHasta]);

  useEffect(() => {
    let vivo = true;
    const cargar = () => api.get(`/analytics/resumen?${paramsPeriodo()}`).then(r => { if (vivo) setResumen(r.data); }).catch(() => {});
    setResumen(null); cargar();
    const iv = setInterval(cargar, 60000);
    return () => { vivo = false; clearInterval(iv); };
  }, [paramsPeriodo]);

  // Cargar analytics cuando cambian las campañas seleccionadas, periodo o fechas
  useEffect(() => {
    if (!campSelIds.length) return;
    setLoadingAn(true);
    const params = new URLSearchParams();
    params.append('campana_ids', campSelIds.join(','));

    // Calcular fechas según periodo activo
    const hoy = new Date();
    if (periodo === 'hoy') {
      const d = new Date(); d.setHours(0,0,0,0);
      params.append('fecha_desde', d.toISOString());
    } else if (periodo === 'semana') {
      const d = new Date(); d.setDate(d.getDate()-7);
      params.append('fecha_desde', d.toISOString());
    } else if (periodo === 'mes') {
      const d = new Date(); d.setDate(d.getDate()-30);
      params.append('fecha_desde', d.toISOString());
    } else if (periodo === 'custom') {
      if (filtroFechaDesde) params.append('fecha_desde', filtroFechaDesde);
      if (filtroFechaHasta) params.append('fecha_hasta', filtroFechaHasta + 'T23:59:59');
    }

    api.get(`/analytics/campanas?${params}`).then(r => {
      setAnalytics(r.data);
    }).catch(() => {}).finally(() => setLoadingAn(false));
  }, [campSelIds, periodo, filtroFechaDesde, filtroFechaHasta]);

  // Rango de fechas
  const { desde, hasta } = useMemo(() => {
    const hoy = new Date(); hoy.setHours(23,59,59);
    if (periodo === 'hoy')   { const d=new Date(); d.setHours(0,0,0,0); return {desde:d,hasta:hoy}; }
    if (periodo === 'semana'){ const d=new Date(); d.setDate(d.getDate()-7); return {desde:d,hasta:hoy}; }
    if (periodo === 'mes')   { const d=new Date(); d.setDate(d.getDate()-30); return {desde:d,hasta:hoy}; }
    return {desde:null,hasta:hoy};
  }, [periodo]);

  const llamadasPeriodo = useMemo(() => llamadas.filter(c => {
    if (!c.date_updated) return false;
    const d = new Date(c.date_updated);
    if (desde && d < desde) return false;
    if (hasta && d > hasta) return false;
    return true;
  }), [llamadas, desde, hasta]);

  const llamadasMostradas = useMemo(() => {
    if (!clickFiltro) return llamadasPeriodo;
    return llamadasPeriodo.filter(c => {
      if (clickFiltro.tipo === 'dia') {
        const f = new Date(c.date_updated).toLocaleDateString('es-CO',{day:'2-digit',month:'2-digit'});
        return f === clickFiltro.valor;
      }
      if (clickFiltro.tipo === 'categoria') return clasificarLlamada(c) === clickFiltro.valor;
      if (clickFiltro.tipo === 'resultado') {
        const r = (c.resultado_llamada||'').toLowerCase();
        if (clickFiltro.valor==='Acuerdo')  return r.includes('acuerdo');
        if (clickFiltro.valor==='Rechazo')  return r.includes('rechazo');
        if (clickFiltro.valor==='Silencio') return r.includes('silencio');
        return !r.includes('acuerdo')&&!r.includes('rechazo')&&!r.includes('silencio');
      }
      return true;
    });
  }, [llamadasPeriodo, clickFiltro]);

  // Métricas globales del periodo (servidor, todas las llamadas)
  const metricas = useMemo(() => ({
    total: resumen?.llamadas ?? 0,
    costo: Math.round(resumen?.costo ?? 0),
    acuerdos: resumen?.acuerdos ?? 0,
    mins: resumen?.minutos_facturados ?? 0,
    tasa: resumen?.tasa_acuerdo ?? 0,
  }), [resumen]);

  const llamadasPorDia = useMemo(() => (resumen?.por_dia || []).map(d => {
    const [, m, dd] = d.fecha.split('-');
    return { fecha: `${dd}/${m}`, total: d.llamadas, acuerdos: d.acuerdos };
  }), [resumen]);

  const tasaResultados = useMemo(() => resumen?.resultados_lista || [], [resumen]);

  const tipificacionesResultados = useMemo(() => {
    const total = resumen?.llamadas || 0;
    if (!total) return [];
    return [...(resumen?.resultados_lista || [])].sort((a,b) => b.value - a.value).map(r => ({
      ...r, pct: Math.round(r.value / total * 100), label: `${r.name} (${Math.round(r.value / total * 100)}%)`,
    }));
  }, [resumen]);

  // Analytics: datos para radar comparativo
  const radarData = useMemo(() => {
    if (!analytics.length) return [];
    return [
      { metrica:'Contacto',    ...Object.fromEntries(analytics.map(a=>[a.nombre, a.tasa_contacto])) },
      { metrica:'Acuerdo',     ...Object.fromEntries(analytics.map(a=>[a.nombre, a.tasa_acuerdo])) },
      { metrica:'Efectividad', ...Object.fromEntries(analytics.map(a=>[a.nombre, a.efectividad_neta])) },
      { metrica:'Penetración', ...Object.fromEntries(analytics.map(a=>[a.nombre, a.tasa_penetracion])) },
      { metrica:'Anti-Rechazo',...Object.fromEntries(analytics.map(a=>[a.nombre, 100-a.tasa_rechazo])) },
    ];
  },[analytics]);

  const statusCfg = {
    Finalizada:   {label:'Completada', color:'var(--success)',bg:'var(--success-soft)'},
    Fallida:      {label:'Fallida',    color:'var(--danger)',bg:'var(--danger-soft)'},
    'No contesta':{label:'No contesta',color:'var(--warning)',bg:'rgba(251,146,60,0.1)'},
    Buzon:        {label:'Buzón',      color:'var(--accent)',bg:'rgba(192,132,252,0.1)'},
    Rechazada:    {label:'Rechazada',  color:'var(--danger)',bg:'rgba(244,63,94,0.1)'},
  };

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:24,display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontFamily:'Inter,sans-serif',fontSize:22,fontWeight:700,color:'var(--text)'}}>
            Bienvenido, {empresa} 👋
          </h1>
          <p style={{color:'var(--text-3)',fontSize:13,marginTop:4}}>
            {lastUpdate ? `↻ ${lastUpdate.toLocaleTimeString('es-CO')}` : 'Cargando...'}
            {clickFiltro && <span style={{color:'var(--accent-solid)',marginLeft:12}}>
              Filtro: {clickFiltro.etiqueta || clickFiltro.valor}
              <span onClick={()=>setClickFiltro(null)} style={{cursor:'pointer',marginLeft:6,color:'var(--danger)'}}>× limpiar</span>
            </span>}
          </p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <Segmented options={PERIODOS.map(p=>({value:p.id,label:p.label}))} value={periodo} onChange={setPeriodo} size="sm" />
          {periodo==='custom' && <>
            <input type="date" value={filtroFechaDesde} onChange={e=>setFiltroFechaDesde(e.target.value)}
              style={{padding:'5px 8px',background:'var(--surface-3)',border:'1px solid var(--surface)',borderRadius:7,color:'var(--text)',fontSize:11,outline:'none'}}/>
            <span style={{color:'var(--text-3)',fontSize:11}}>→</span>
            <input type="date" value={filtroFechaHasta} onChange={e=>setFiltroFechaHasta(e.target.value)}
              style={{padding:'5px 8px',background:'var(--surface-3)',border:'1px solid var(--surface)',borderRadius:7,color:'var(--text)',fontSize:11,outline:'none'}}/>
          </>}
          <button onClick={()=>exportCSV(llamadasMostradas.map(c=>({
            nombre:c.nombre,telefono:c.telefono,duracion:c.duracion_segundos,
            estado:c.status,resultado:c.resultado_llamada,costo:c.costo_llamada,
            fecha:c.date_updated,
          })),'llamadas.csv')} style={{
            padding:'6px 12px',borderRadius:7,border:'1px solid var(--surface)',
            background:'var(--surface-3)',color:'var(--text-2)',fontSize:12,cursor:'pointer',
          }}>↓ Exportar</button>
        </div>
      </div>

      {/* Métricas globales */}
      <div className="r-kpi" style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,marginBottom:24}}>
        <Card label="Saldo actual"   value={billing?fmt(billing.saldo_actual,'cop'):'—'} color="var(--accent-solid)" sub="COP disponible"/>
        <Card label="Llamadas"       value={metricas.total}    color="var(--text)" sub="en el periodo"/>
        <Card label="Minutos"        value={metricas.mins}     color="var(--text)" sub="facturados"/>
        <Card label="Costo periodo"  value={fmt(metricas.costo,'cop')} color="var(--text)" sub="COP"/>
        <Card label="Tasa acuerdo"   value={fmt(metricas.tasa,'pct')} color="var(--text)" sub="de quienes hablaron"/>
      </div>

      {/* Conversión / PTP rate por campaña (base del "agente que se mejora solo") */}
      {conv && conv.por_campana && conv.por_campana.length > 0 && (
        <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:20,marginBottom:24}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4,flexWrap:'wrap',gap:8}}>
            <span style={{fontSize:15,fontWeight:700,color:'var(--text)'}}>🎯 Conversión por campaña (PTP)</span>
            <span style={{fontSize:12,color:'var(--text-3)'}}>PTP rate global: <b style={{color:'var(--accent)'}}>{conv.resumen?.ptp_rate_global}%</b> · {conv.resumen?.ptp} acuerdos / {conv.resumen?.contactadas} contactadas</span>
          </div>
          <div style={{fontSize:11,color:'var(--text-3)',marginBottom:14}}>Qué prompt/voz recupera más. Ordenado por tasa de acuerdos sobre contactadas.</div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{color:'var(--text-3)',textAlign:'left'}}>
                  <th style={{padding:'6px 8px',fontWeight:600}}>Campaña</th>
                  <th style={{padding:'6px 8px',fontWeight:600,textAlign:'right'}}>Llamadas</th>
                  <th style={{padding:'6px 8px',fontWeight:600,textAlign:'right'}}>Contacto</th>
                  <th style={{padding:'6px 8px',fontWeight:600,textAlign:'right'}}>PTP</th>
                  <th style={{padding:'6px 8px',fontWeight:600,textAlign:'right'}}>PTP rate</th>
                  <th style={{padding:'6px 8px',fontWeight:600,textAlign:'right'}}>Costo/PTP</th>
                </tr>
              </thead>
              <tbody>
                {conv.por_campana.filter(c=>c.total>=5).slice(0,12).map((c,i)=>(
                  <tr key={i} style={{borderTop:'1px solid var(--surface)'}}>
                    <td style={{padding:'7px 8px',color:'var(--text)',fontWeight:500}}>{c.campana}</td>
                    <td style={{padding:'7px 8px',textAlign:'right',color:'var(--text-3)'}}>{c.total}</td>
                    <td style={{padding:'7px 8px',textAlign:'right',color:'var(--text-3)'}}>{c.contacto_rate}%</td>
                    <td style={{padding:'7px 8px',textAlign:'right',color:'var(--text-3)'}}>{c.ptp}</td>
                    <td style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color: c.ptp_rate>=15?'var(--success)':(c.ptp_rate>=5?'var(--warning)':'var(--text-3)')}}>{c.ptp_rate}%</td>
                    <td style={{padding:'7px 8px',textAlign:'right',color:'var(--text-3)'}}>{c.costo_por_ptp?`$${Number(c.costo_por_ptp).toLocaleString('es-CO')}`:'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gráficas globales */}
      <div className="r-apila" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:28}}>
        {/* Llamadas por día */}
        <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Llamadas por día</div>
              <div style={{fontSize:11,color:'var(--text-3)',marginTop:2}}>Clic para filtrar tabla</div>
            </div>
            <Segmented options={[{value:'area',label:'Área'},{value:'bar',label:'Barras'}]} value={chartType} onChange={setChartType} size="sm" />
          </div>
          <ResponsiveContainer width="100%" height={180}>
            {chartType==='area'?(
              <AreaChart data={llamadasPorDia} onClick={d=>d?.activePayload&&setClickFiltro({tipo:'dia',valor:d.activeLabel})}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-solid)" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="var(--accent-solid)" stopOpacity={0}/>
                </linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface)"/>
                <XAxis dataKey="fecha" tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
                <Tooltip content={<Tooltip2/>}/>
                <Area type="monotone" dataKey="total" name="Llamadas" stroke="var(--accent-solid)" fill="url(#g1)" strokeWidth={2}/>
              </AreaChart>
            ):(
              <BarChart data={llamadasPorDia} onClick={d=>d?.activePayload&&setClickFiltro({tipo:'dia',valor:d.activeLabel})}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface)"/>
                <XAxis dataKey="fecha" tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
                <Tooltip content={<Tooltip2/>}/>
                <Bar dataKey="total" name="Llamadas" fill="var(--accent-solid)" radius={[4,4,0,0]}/>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Resultados */}
        <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:20}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Resultados</div>
            <div style={{fontSize:11,color:'var(--text-3)',marginTop:2}}>Todas las llamadas del periodo · clic para filtrar las últimas</div>
          </div>
          {tasaResultados.length===0?(
            <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-3)',fontSize:13}}>Sin datos</div>
          ):(
            <>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={tasaResultados} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                  dataKey="value" paddingAngle={3}
                  onClick={d=>setClickFiltro({tipo:'categoria',valor:d.clave||d.payload?.clave,etiqueta:d.name})}>
                  {tasaResultados.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} style={{cursor:'pointer'}}/>)}
                </Pie>
                <Tooltip content={<Tooltip2/>}/>
                <Legend iconType="circle" iconSize={8} formatter={v=><span style={{color:'var(--text-2)',fontSize:12}}>{v}</span>}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:5}}>
              {tipificacionesResultados.map((t,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 8px',borderRadius:6,background:'rgba(255,255,255,0.03)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:COLORS[i%COLORS.length]}}/>
                    <span style={{fontSize:11,color:'var(--text-2)'}}>{t.name}</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:11,color:'var(--text-3)'}}>{t.value}</span>
                    <span style={{fontSize:12,fontWeight:700,color:COLORS[i%COLORS.length]}}>{t.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </div>


      {/* ── SENTIMIENTOS Y RESULTADOS POR HORA ── */}
      <div className="r-apila" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
        <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:20}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text)',marginBottom:4}}>Sentimientos</div>
          <div style={{fontSize:11,color:'var(--text-3)',marginBottom:16}}>Ánimo de quienes hablaron con el agente</div>
          {(()=>{
            const tot=resumen?.sentimientos||{positivo:0,neutro:0,negativo:0};
            const total=tot.positivo+tot.neutro+tot.negativo;
            const data=[
              {name:'Positivo',value:tot.positivo,color:'var(--success)'},
              {name:'Neutro',value:tot.neutro,color:'var(--text-2)'},
              {name:'Negativo',value:tot.negativo,color:'var(--danger)'},
            ];
            if(!total)return <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-3)',fontSize:13}}>Sin datos</div>;
            return(<div>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                    {data.map((s,i)=><Cell key={i} fill={s.color}/>)}
                  </Pie>
                  <Tooltip content={<Tooltip2/>}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{display:'flex',justifyContent:'center',gap:16,marginTop:8}}>
                {data.map(s=>(<div key={s.name} style={{textAlign:'center'}}>
                  <div style={{fontSize:18,fontWeight:700,color:s.color}}>{total?Math.round(s.value/total*100):0}%</div>
                  <div style={{fontSize:10,color:'var(--text-3)'}}>{s.name}</div>
                </div>))}
              </div>
            </div>);
          })()}
        </div>
        <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:20}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text)',marginBottom:4}}>Contacto por hora del día</div>
          <div style={{fontSize:11,color:'var(--text-3)',marginBottom:16}}>¿A qué hora contestan más?</div>
          {(()=>{
            const horaData=resumen?.por_hora||[];
            if(!horaData.length)return <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-3)',fontSize:13}}>Sin datos</div>;
            return(<ResponsiveContainer width="100%" height={180}>
              <BarChart data={horaData} margin={{top:0,right:0,bottom:0,left:-20}}>
                <XAxis dataKey="hora" tick={{fontSize:10,fill:'var(--text-3)'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:'var(--text-3)'}} axisLine={false} tickLine={false}/>
                <Tooltip content={<Tooltip2/>}/>
                <Bar dataKey="total" name="Llamadas" fill="rgba(124,58,237,0.35)" radius={[3,3,0,0]}/>
                <Bar dataKey="contestadas" name="Contestadas" fill="rgba(124,58,237,0.8)" radius={[3,3,0,0]}/>
                <Bar dataKey="acuerdos" name="Acuerdos" fill="var(--success)" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>);
          })()}
        </div>
      </div>
      {/* ── ANALYTICS AVANZADOS POR CAMPAÑA ── */}
      <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,padding:24,marginBottom:24}}>
        <div className="r-col" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:16,marginBottom:20}}>
          <div>
            <div style={{fontFamily:'Inter,sans-serif',fontSize:16,fontWeight:700,color:'var(--text)'}}>Analytics por campaña</div>
            <div style={{fontSize:12,color:'var(--text-3)',marginTop:2}}>Selecciona una o más campañas para comparar</div>
          </div>
          {/* Selector de campañas */}
          <div className="r-chips" style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end',maxHeight:118,overflowY:'auto',maxWidth:760}}>
            {campanas.map((c,i)=>{
              const sel = campSelIds.includes(c.id);
              return (
                <button key={c.id} onClick={()=>setCampSelIds(prev=>sel?prev.filter(x=>x!==c.id):[...prev,c.id])} style={{
                  padding:'5px 12px',borderRadius:20,border:`1px solid ${sel?COLORS[i%COLORS.length]:'var(--surface-2)'}`,
                  background:sel?`${COLORS[i%COLORS.length]}22`:'transparent',
                  color:sel?COLORS[i%COLORS.length]:'var(--text-3)',fontSize:11,cursor:'pointer',
                  transition:'all 0.2s',
                }}>{c.nombre_campana}</button>
              );
            })}
          </div>
        </div>

        {/* Tabs de vista */}
        <div style={{marginBottom:20}}>
          <Segmented
            options={[{value:'resumen',label:'Resumen'},{value:'comparar',label:'Comparar'},{value:'barridos',label:'Barridos'},{value:'evolucion',label:'Evolución'}]}
            value={vistaAnalytics} onChange={setVistaAnalytics} size="sm" />
        </div>

        {loadingAn && <div style={{color:'var(--text-3)',fontSize:13,padding:20,textAlign:'center'}}>Calculando métricas...</div>}

        {!loadingAn && analytics.length === 0 && (
          <div style={{color:'var(--text-3)',fontSize:13,padding:20,textAlign:'center'}}>Sin datos para las campañas seleccionadas</div>
        )}

        {/* VISTA: RESUMEN */}
        {!loadingAn && vistaAnalytics==='resumen' && analytics.map((a,i)=>(
          <div key={a.campana_id} style={{marginBottom:20,padding:20,background:'var(--surface-3)',borderRadius:12,border:`1px solid ${COLORS[i%COLORS.length]}33`}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:6,marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:700,color:COLORS[i%COLORS.length]}}>{a.nombre}</div>
              <div style={{fontSize:11,color:'var(--text-3)'}}>{a.total} contactos · {a.llamadas} llamadas</div>
            </div>
            <div className="r-kpi" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
              {[
                {label:'Tasa contacto',    value:fmt(a.tasa_contacto,'pct'),     color:'var(--text)'},
                {label:'Tasa acuerdo',     value:fmt(a.tasa_acuerdo,'pct'),      color:'var(--text)'},
                {label:'Efectividad neta', value:fmt(a.efectividad_neta,'pct'),  color:'var(--text)'},
                {label:'Penetración',      value:fmt(a.tasa_penetracion,'pct'),  color:'var(--text)'},
                {label:'Costo total',      value:fmt(a.costo_total,'cop'),       color:'var(--text)'},
                {label:'Costo x contacto', value:fmt(a.costo_x_contacto,'cop'), color:'var(--text)'},
                {label:'Costo x acuerdo',  value:fmt(a.costo_x_acuerdo,'cop'),  color:'var(--text)'},
                {label:'Deuda con acuerdo', value:a.deuda_con_acuerdo>0?fmt(a.deuda_con_acuerdo,'cop'):'—', color:'var(--text)'},
              ].map((m,j)=>(
                <div key={j} style={{background:'var(--bg)',borderRadius:8,padding:'10px 14px'}}>
                  <div style={{fontSize:10,color:'var(--text-3)',marginBottom:4,display:'flex',alignItems:'center'}}>
                    {m.label}<MetricaTooltip label={m.label}/>
                  </div>
                  <div style={{fontSize:16,fontWeight:700,color:m.color,fontVariantNumeric:'tabular-nums'}}>{m.value}</div>
                </div>
              ))}
            </div>
            {/* Barra de estados */}
            <div style={{marginTop:8}}>
              <div style={{fontSize:11,color:'var(--text-3)',marginBottom:8}}>Resultado de las llamadas</div>
              {(()=>{
                const r=a.resultados||{};
                const partes=[
                  {count:r.acuerdo,     color:'var(--success)',      label:'Acuerdo'},
                  {count:r.contesto,    color:'var(--accent-solid)', label:'Contestó sin acuerdo'},
                  {count:r.sin_hablar,  color:'var(--accent)',       label:'Contestó sin hablar'},
                  {count:r.buzon,       color:'rgba(192,132,252,0.45)', label:'Buzón'},
                  {count:r.no_contesto, color:'var(--warning)',      label:'No contestó'},
                  {count:r.rechazo,     color:'var(--danger)',       label:'Rechazó la llamada'},
                ].filter(s=>s.count>0);
                return (<>
                  <div style={{display:'flex',borderRadius:6,overflow:'hidden',height:20}}>
                    {partes.map((s,j)=>(
                      <div key={j} title={`${s.label}: ${s.count}`} style={{flex:s.count,background:s.color,opacity:0.85,transition:'flex 0.3s'}}/>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:12,marginTop:8,flexWrap:'wrap'}}>
                    {partes.map((s,j)=>(
                      <div key={j} style={{fontSize:11,color:'var(--text-2)',display:'flex',alignItems:'center',gap:5}}>
                        <span style={{width:8,height:8,borderRadius:'50%',background:s.color,display:'inline-block'}}/>
                        {s.label}: <strong style={{color:'var(--text)'}}>{s.count}</strong>
                      </div>
                    ))}
                  </div>
                </>);
              })()}
            </div>
          </div>
        ))}

        {/* VISTA: COMPARAR (Radar) */}
        {!loadingAn && vistaAnalytics==='comparar' && analytics.length >= 1 && (
          <div>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--surface-2)"/>
                <PolarAngleAxis dataKey="metrica" tick={{fill:'var(--text-3)',fontSize:11}}/>
                {analytics.map((a,i)=>(
                  <Radar key={a.campana_id} name={a.nombre} dataKey={a.nombre}
                    stroke={COLORS[i%COLORS.length]} fill={COLORS[i%COLORS.length]} fillOpacity={0.15}/>
                ))}
                <Legend iconType="circle" iconSize={8} formatter={v=><span style={{color:'var(--text-2)',fontSize:12}}>{v}</span>}/>
                <Tooltip content={<Tooltip2/>}/>
              </RadarChart>
            </ResponsiveContainer>
            {/* Tabla comparativa */}
            <div style={{marginTop:16,overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:'1px solid var(--surface-2)'}}>
                    <th style={{padding:'8px 12px',textAlign:'left',color:'var(--text-3)',fontWeight:500}}>Métrica</th>
                    {analytics.map((a,i)=>(
                      <th key={i} style={{padding:'8px 12px',textAlign:'right',color:COLORS[i%COLORS.length],fontWeight:600}}>{a.nombre}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {label:'Total contactos',    key:'total',           fmt:'num'},
                    {label:'Tasa contacto',      key:'tasa_contacto',   fmt:'pct'},
                    {label:'Tasa acuerdo',       key:'tasa_acuerdo',    fmt:'pct'},
                    {label:'Efectividad neta',   key:'efectividad_neta',fmt:'pct'},
                    {label:'Costo total',        key:'costo_total',     fmt:'cop'},
                    {label:'Costo x acuerdo',    key:'costo_x_acuerdo', fmt:'cop'},
                    {label:'Deuda con acuerdo',  key:'deuda_con_acuerdo', fmt:'cop'},
                  ].map((row,j)=>(
                    <tr key={j} style={{borderBottom:'1px solid var(--surface-3)'}}>
                      <td style={{padding:'8px 12px',color:'var(--text-3)'}}>{row.label}</td>
                      {analytics.map((a,i)=>(
                        <td key={i} style={{padding:'8px 12px',textAlign:'right',color:'var(--text)',fontVariantNumeric:'tabular-nums'}}>
                          {fmt(a[row.key],row.fmt)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* VISTA: BARRIDOS */}
        {!loadingAn && vistaAnalytics==='barridos' && analytics.map((a,i)=>{
          const barridosData = Object.entries(a.barridos).sort(([x],[y])=>parseInt(x)-parseInt(y)).map(([num,d])=>({
            barrido: `Barrido ${num}`,
            total: d.total,
            acuerdos: d.acuerdos,
            finalizadas: d.finalizadas,
            costo: Math.round(d.costo),
            efectividad: d.total>0 ? Math.round(d.acuerdos/d.total*100) : 0,
          }));
          return (
            <div key={a.campana_id} style={{marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:600,color:COLORS[i%COLORS.length],marginBottom:12}}>{a.nombre}</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barridosData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--surface)"/>
                  <XAxis dataKey="barrido" tick={{fill:'var(--text-3)',fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<Tooltip2/>}/>
                  <Legend iconType="circle" iconSize={8} formatter={v=><span style={{color:'var(--text-2)',fontSize:12}}>{v}</span>}/>
                  <Bar dataKey="total"      name="Total"     fill="var(--text-3)" radius={[4,4,0,0]}/>
                  <Bar dataKey="finalizadas" name="Contactadas" fill={COLORS[i%COLORS.length]} radius={[4,4,0,0]}/>
                  <Bar dataKey="acuerdos"   name="Acuerdos"  fill="var(--success)" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })}

        {/* VISTA: EVOLUCIÓN */}
        {!loadingAn && vistaAnalytics==='evolucion' && (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart>
              <defs>
                {analytics.map((a,i)=>(
                  <linearGradient key={i} id={`grad${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[i%COLORS.length]} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS[i%COLORS.length]} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface)"/>
              <XAxis dataKey="fecha" tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'var(--text-3)',fontSize:10}} axisLine={false} tickLine={false}/>
              <Tooltip content={<Tooltip2/>}/>
              <Legend iconType="circle" iconSize={8} formatter={v=><span style={{color:'var(--text-2)',fontSize:12}}>{v}</span>}/>
              {analytics.map((a,i)=>(
                <Area key={a.campana_id} data={a.evolucion} type="monotone"
                  dataKey="llamadas" name={a.nombre}
                  stroke={COLORS[i%COLORS.length]} fill={`url(#grad${i})`} strokeWidth={2}/>
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Llamadas recientes */}
      <div className="ic-card" style={{background:'var(--bg)',border:'1px solid var(--surface)',borderRadius:16,overflow:'hidden'}}>
        <div style={{padding:'18px 20px',borderBottom:'1px solid var(--surface)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>
            {clickFiltro ? `Llamadas — ${clickFiltro.valor}` : 'Llamadas recientes'}
            <span style={{color:'var(--text-3)',fontWeight:400,marginLeft:8,fontSize:12}}>({llamadasMostradas.length})</span>
          </span>
          <span onClick={()=>navigate('/calls')} style={{fontSize:12,color:'var(--accent-solid)',cursor:'pointer'}}>Ver todas →</span>
        </div>
        {llamadasMostradas.length===0&&<div style={{padding:24,textAlign:'center',color:'var(--text-3)',fontSize:13}}>No hay llamadas</div>}
        {llamadasMostradas.slice(0,8).map(c=>{
          const cfg=statusCfg[c.status]||{label:c.status,color:'var(--text-2)',bg:'rgba(148,163,184,0.1)'};
          return(
            <div key={c.id} style={{padding:'12px 20px',borderBottom:'1px solid var(--surface-3)',display:'flex',alignItems:'center',gap:16}}>
              <div style={{width:32,height:32,borderRadius:'50%',background:'var(--accent-soft)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,color:'var(--accent-solid)',fontWeight:700,flexShrink:0}}>
                {(c.nombre||'?')[0]}
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:500,fontSize:13,color:'var(--text)'}}>{c.nombre}</div>
                <div style={{fontSize:11,color:'var(--text-3)'}}>{c.telefono} · {new Date(c.date_updated).toLocaleString('es-CO',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</div>
              </div>
              <div style={{fontSize:11,color:'var(--text-3)'}}>{c.duracion_segundos}</div>
              {c.costo_llamada&&<div style={{fontSize:11,color:'var(--warning)'}}>${parseFloat(c.costo_llamada).toLocaleString('es-CO')}</div>}
              <div style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:500,color:cfg.color,background:cfg.bg}}>{cfg.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
